import { useEffect, useRef, useState, type FormEvent } from "react";
import { useQuery } from "@tanstack/react-query";

// Website live chat. Messages relay to the operator's Telegram; replies are
// polled while the panel is open. The thread token lives in this browser only.
type ChatMessage = { id: number; sender: "visitor" | "operator"; text: string; created_at: number };
const TOKEN_KEY = "trimlog.chat.token";

function readToken(): string {
  try { return localStorage.getItem(TOKEN_KEY) ?? ""; } catch { return ""; }
}

export function ChatWidget() {
  const { data: status } = useQuery({
    queryKey: ["chat-status"],
    queryFn: async () => (await fetch("/api/chat/status")).json() as Promise<{ enabled: boolean }>,
    staleTime: 300_000, retry: false,
  });
  const [open, setOpen] = useState(false);
  const [token, setToken] = useState(readToken);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [text, setText] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const lastId = useRef(0);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open || !token) return;
    let stop = false;
    const poll = async () => {
      const res = await fetch(`/api/chat?token=${encodeURIComponent(token)}&after=${lastId.current}`).catch(() => null);
      if (stop || !res) return;
      if (res.status === 404) { try { localStorage.removeItem(TOKEN_KEY); } catch { /* storage blocked */ } setToken(""); return; }
      const body = await res.json().catch(() => ({ messages: [] })) as { messages: ChatMessage[] };
      if (body.messages?.length) {
        lastId.current = body.messages[body.messages.length - 1].id;
        setMessages((m) => [...m, ...body.messages.filter((x) => !m.some((y) => y.id === x.id))]);
      }
    };
    void poll();
    const timer = setInterval(() => void poll(), 4000);
    return () => { stop = true; clearInterval(timer); };
  }, [open, token]);

  useEffect(() => { listRef.current?.scrollTo({ top: listRef.current.scrollHeight }); }, [messages, open]);

  if (!status?.enabled) return null;

  async function send(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const msg = text.trim();
    if (!msg || busy) return;
    setBusy(true); setError("");
    try {
      const res = await fetch("/api/chat", {
        method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: token || undefined, text: msg, name: name.trim() || undefined, email: email.trim() || undefined, page: window.location.pathname }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) { setError(body.error || "Message not sent. Please try again."); return; }
      if (body.token && body.token !== token) {
        try { localStorage.setItem(TOKEN_KEY, body.token); } catch { /* storage blocked: chat lasts this page only */ }
        setToken(body.token);
      }
      setMessages((m) => [...m, { id: body.id, sender: "visitor", text: msg, created_at: Math.floor(Date.now() / 1000) }]);
      lastId.current = Math.max(lastId.current, body.id);
      setText("");
      if (body.delayed) setError("Sent. Replies may take a little longer than usual.");
    } catch { setError("Could not reach Trim Log. Check your connection."); }
    finally { setBusy(false); }
  }

  return <div className="site-chat">
    {open && <section className="site-chat-panel" aria-label="Live chat">
      <header><div><strong>Chat with Trim Log</strong><span>We usually reply within minutes in NZ business hours.</span></div>
        <button type="button" aria-label="Close chat" onClick={() => setOpen(false)}>×</button></header>
      <div className="site-chat-list" ref={listRef} aria-live="polite">
        {!messages.length && <p className="site-chat-hint">Questions about Trim Log, pricing or getting your crew set up? Ask here.</p>}
        {messages.map((m) => <p key={m.id} className={`site-chat-msg ${m.sender}`}>{m.text}</p>)}
      </div>
      <form onSubmit={send}>
        {!token && <div className="site-chat-who">
          <input aria-label="Your name (optional)" placeholder="Name (optional)" maxLength={80} value={name} onChange={(e) => setName(e.target.value)} />
          <input aria-label="Your email (optional)" type="email" placeholder="Email, so we can reply if you leave" maxLength={254} value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>}
        {error && <p className="site-chat-error" role="status">{error}</p>}
        <div className="site-chat-send">
          <textarea aria-label="Message" placeholder="Type a message…" rows={2} maxLength={2000} value={text} onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); e.currentTarget.form?.requestSubmit(); } }} />
          <button type="submit" className="site-button site-button-small" disabled={busy || !text.trim()}>Send</button>
        </div>
      </form>
    </section>}
    <button type="button" className="site-chat-toggle" aria-expanded={open} onClick={() => setOpen(!open)}>
      {open ? "Close chat" : "💬 Chat with us"}
    </button>
  </div>;
}
